import {
    Component,
    computed,
    inject,
    Input,
    OnInit,
    signal
} from "@angular/core";
import { VisualizationOptionsType } from "@arch/_shared/types/options-visualization";
import {
    CompanyEntity,
    PointEntity,
    SerieEntity,
    VisualizationEntity
} from "@arch/domain/entity";
import { ProgressBarComponent } from "../../dsn/progress-bar/progress-bar.component";
import { FlexLayoutModule } from "@angular/flex-layout";
import { ComparationPercentageValueComponent } from "../../comparation-percentage-value/comparation-percentage-value.component";
import { GetIndicatorComparationsController } from "@arch/presentation/controllers";
import { ResponseComparationType } from "@arch/infra/repository/map";
import { IndicatorComparationsResultType } from "@arch/domain/application/usecase";
import { DateUtils } from "@arch/_shared/utils";
import { FormatNumbers } from "@arch/_shared/utils/format-number.utils";
import { SERIE_REPOSITORY } from "@app/app.providers";
import { HighchartsRenderComponent } from "../../highcharts-render/highcharts-render.component";
import { ColorsService } from "@arch/domain/application/services/colors.service";

@Component({
    selector: "app-special-comparation",
    imports: [
        ProgressBarComponent,
        FlexLayoutModule,
        ComparationPercentageValueComponent,
        HighchartsRenderComponent
    ],
    templateUrl: "./special-comparation.component.html",
    styleUrl: "./special-comparation.component.scss"
})
export class SpecialComparationComponent implements OnInit {
    @Input() visualization: VisualizationEntity;
    @Input() series: SerieEntity[];
    @Input() points: PointEntity[];
    @Input() company?: CompanyEntity;
    @Input() set options(options: VisualizationOptionsType) {
        this.optionsSignal.set(options);
    }
    get options() {
        return this.optionsSignal();
    }

    public isCustomStructure: boolean = true;
    private readonly optionsSignal = signal<VisualizationOptionsType>(null);

    private readonly getIndicatorComparationsController = inject(
        GetIndicatorComparationsController
    );
    private readonly serieMapRepository = inject(SERIE_REPOSITORY);

    public readonly comparations = computed(() => {
        const series = this.serieMapRepository.getByIds(
            this.visualization.props.series
        );
        const response = this.getIndicatorComparationsController.execute({
            company: this.company,
            series,
            options: this.options
        });
        const comparations: ComparationsType[] = [];
        for (let comparation of response) {
            comparations.push({
                ...comparation,
                valuePercentageCurrencyString: FormatNumbers.byUnit(
                    "currency",
                    comparation.value,
                    2
                ),
                polarityIcon: this.getPolarityIcon(comparation),
                polarityColorByConfig: ColorsService.getColor(
                    comparation.serie.getPolarityColorByConfig(
                        this.visualization
                    )
                ),
                percentageLocaleString: FormatNumbers.byUnit(
                    "percentage",
                    comparation.percentage * 100,
                    2
                ),
                currencyString: FormatNumbers.byUnit(
                    "currency",
                    comparation.current,
                    2
                )
            });
        }

        return comparations.sort((a, b) => a.serie.props.i - b.serie.props.i);
    });

    ngOnInit(): void {
        this.setVisualization();
    }

    private setVisualization(): void {
        if (
            this.options.comparation.active ||
            this.options.budget ||
            this.options.projection ||
            this.options.goals
        ) {
            this.isCustomStructure = false;
            this.setVisualizationChart();
        }
    }

    private setVisualizationChart(): void {
        const getSeriesReference = () => {
            const seriesReferences = this.visualization.props?.series;
            return seriesReferences.map((serieId, i) => {
                const serie =
                    this.visualization.props.component.configs.series.find(
                        (s: any) => s._id === serieId
                    );

                return {
                    _id: serie._id,
                    color: serie.color
                };
            });
        };

        const struture: any = {
            title: {
                text: ""
            },
            credits: {
                enabled: false
            },
            exporting: {
                enabled: false
            },
            chart: {
                type: "column"
            },
            plotOptions: {
                series: {
                    borderWidth: 0
                }
            },
            yAxis: { title: { text: "" } },
            legend: {
                enabled: true
            },
            series: getSeriesReference()
        };

        if (
            this.options.goals &&
            this.visualization.props?.component?.configs?.goals?.length
        ) {
            struture.goals =
                this.visualization.props?.component?.configs?.goals.map(
                    (goal: any) => {
                        goal.type = "line";
                        goal.color = goal.color?.replace("-", ".");
                        return goal;
                    }
                );
        }

        this.visualization.setChartConfiguration(struture);
    }

    private getPolarityIcon(comparation: ResponseComparationType): string {
        if (comparation.value === 0) return "fa-solid fa-minus";
        if (comparation?.variationCondition) return "fa-solid fa-sort-up";
        else return "fa-solid fa-sort-down";
    }

    public readonly getComparationLabel = computed(() => {
        return DateUtils.getComparationLabel(
            { period: this.options.period, date: this.options.endDate },
            {
                period: this.options.comparation.period,
                date: this.options.comparation.date
            }
        );
    });
}

export interface ISpecialComparationComponent {
    series: SerieEntity[];
    points: PointEntity[];
    visualization: VisualizationEntity;
    options: VisualizationOptionsType;
    company?: CompanyEntity;
}

type ComparationsType = IndicatorComparationsResultType & {
    valuePercentageCurrencyString?: string;
    polarityIcon?: string;
    colorPolarity?: string;
    percentageLocaleString?: string;
    polarityColorByConfig?: string;
    currencyString?: string;
};
